/**
 * roomTemp.js — the room's running price temperature, in dollars.
 *
 * ONE implementation of the inflation tally, shared by three surfaces so they
 * can never disagree:
 *
 *   - src/engine/simulation.js  (simulateDraftSync — the batch-sim / auto-finish loop)
 *   - src/hooks/useMockDraft.js (the browser auction loop)
 *   - the Live Board          (manual entry of a real auction)
 *
 * The quantity is the same one the engine has always fed to calcAiMaxBidV2 as
 * `inflationIndex`:
 *
 *     tally += salePrice - tierMidDollars(player)
 *
 * i.e. the signed dollar sum of how much more (or less) the room has paid than
 * the tier bands say those players are worth. Positive = money is going out
 * faster than the board expects.
 *
 * ── 🔴 UNITS ────────────────────────────────────────────────────────
 * `floor` / `ceiling` in tiers.js are FRACTIONS OF THE BUDGET, not dollars
 * (tiers.js:5). `* budget` is not optional. A prior version compared dollars
 * against a ~0.2 fraction, which made the index approximately cumulative spend:
 * every draft was past the engine's +30 tighten threshold by about pick 3, the
 * deflation branch was unreachable, and the anchor gate (>= tierMid * 0.5) was
 * vacuously true. See the comment at simulation.js:1007.
 *
 * ── 🔴 ±30 IS AN ENGINE THRESHOLD, NOT A HUMAN ONE ──────────────────
 * auction.js:888-896 changes AI bidding at inflationIndex ±30. On a $200 budget
 * that is 15% of one team's entire pool — an enormous move, reached routinely.
 * Never surface ±30 to a user as "hot" or "cold." The human-facing quantities
 * are the signed `tally` and `perSlot` (tally / open slots league-wide); the
 * latter came from the r/ff thread (u/92eph) and is scale-free across budgets
 * and league sizes. ROOM_BANDS below is fitted to real auctions, not intuition
 * — see docs/reference/room-bands-aug10.md.
 */
import { getTierForRank } from '../data/tiers';
import { getEffectivePosRank, parseRankNum } from '../data/adpData';

/**
 * The tier band a player sits in, or null when he is off the board entirely
 * (parseRankNum returns the 999 sentinel for an unresolvable posRank).
 *
 * Callers that need the tier NUMBER as well as the mid — the tier-anchor
 * bookkeeping in simulation.js and useMockDraft.js — read it from here rather
 * than re-deriving the rank.
 */
export function tierForPlayer(player) {
  const rankNum = parseRankNum(getEffectivePosRank(player.name, player.posRank));
  return rankNum < 999 ? getTierForRank(player.pos, rankNum) : null;
}

/**
 * What the board expects this player to cost, in DOLLARS. null when he is
 * unpriced (off the board) — callers must skip those sales rather than treat
 * them as zero, which is what every engine callsite has always done.
 *
 * `dynamicPriceMap.__leagueMultipliers` is the calibrated-league rider
 * (usePricing.js:308): absent for every uncalibrated league, in which case the
 * lookup reads undefined -> 1.0 by construction.
 */
export function tierMidDollars(player, settings, dynamicPriceMap) {
  const tierObj = tierForPlayer(player);
  if (!tierObj) return null;
  const infMult = dynamicPriceMap?.__leagueMultipliers
    ? (dynamicPriceMap.__leagueMultipliers[player.pos] ?? 1) : 1;
  return (tierObj.floor + tierObj.ceiling) / 2 * (settings?.budget || 200) * infMult;
}

// ── Bands ───────────────────────────────────────────────────────────
// Fitted, not chosen: 99 real 2025 MFL PPR auctions (20,838 sales) replayed
// sale by sale against the board those drafters actually had. Method, full
// distribution tables and sensitivity: docs/reference/room-bands-aug10.md.
// Re-fit with `node src/tools/runRoomBands.scratch.js`.
//
// Three properties the numbers carry that intuition does not supply:
//
//   1. perSlot DRIFTS UPWARD through a draft. Median goes -0.02 in the first
//      decile to +1.35 by the ninth: expensive players clear early against tier
//      mids that under-price them, and the $1 tail claws it back. A single pair
//      of fixed cutoffs would label an utterly ordinary late room "hot" on
//      timing alone, so the cutoffs are per-decile of slots filled.
//   2. perSlot IS NOT SCALE-FREE ACROSS BUDGET. It is scale-free across league
//      SIZE by construction — tally and slots both grow with teams — but tierMid
//      and sale prices both scale with the budget, so a $500 league runs ~2.5x
//      the raw perSlot at identical behaviour ($300+ leagues measured p90 of
//      2.70 vs $200's 14.21 at the 80-90% decile before normalising, and 1.35
//      after). Banding therefore happens on the $200-equivalent value.
//   3. THE LAST DECILE IS UNUSABLE. openSlots collapses toward zero and the
//      ratio explodes — IQR 26.60 against 8.79 one decile earlier. Banded null.
export const ROOM_BANDS = {
  // [veryCold(p10), cold(p25), hot(p75), veryHot(p90)] on $200-equivalent
  // perSlot, one row per 10% of league-wide roster slots filled.
  byProgress: [
    [-0.42, -0.20, 0.13, 0.38],   //  0-10% filled, n=1900
    [-0.82, -0.41, 0.55, 1.20],   // 10-20% filled, n=1895
    [-1.02, -0.44, 0.87, 1.99],   // 20-30% filled, n=1880
    [-1.18, -0.37, 1.33, 2.60],   // 30-40% filled, n=1878
    [-1.45, -0.28, 1.84, 3.53],   // 40-50% filled, n=1811
    [-1.51, -0.40, 2.29, 4.84],   // 50-60% filled, n=1822
    [-2.08, -0.97, 2.89, 6.51],   // 60-70% filled, n=1766
    [-3.29, -1.85, 3.71, 8.84],   // 70-80% filled, n=1774
    [-6.86, -3.58, 5.21, 13.70],  // 80-90% filled, n=1719
    // 90-100% deliberately absent — see note 3 above.
  ],
  // Below this many sales there is no room to read yet.
  minSold: 10,
  // The budget the table is expressed in. perSlot is rescaled to it.
  refBudget: 200,
};

/**
 * A coarse label for perSlot, or null when the draft is too early or too late
 * for the ratio to carry information. Descriptive, never prescriptive: "cold"
 * means the room has paid LESS than the board expects, and every surface that
 * renders it says so in those terms.
 *
 * Returns 'very-cold' | 'cold' | 'normal' | 'hot' | 'very-hot' | null.
 */
export function roomBand(perSlot, nSold, openSlots, budget) {
  if (perSlot == null || !(openSlots > 0)) return null;
  if (nSold < ROOM_BANDS.minSold) return null;
  const totalSlots = nSold + openSlots;
  const row = ROOM_BANDS.byProgress[Math.floor((nSold / totalSlots) * 10)];
  if (!row) return null;                          // last decile, or past it
  const at200 = perSlot * (ROOM_BANDS.refBudget / (budget || ROOM_BANDS.refBudget));
  const [veryCold, cold, hot, veryHot] = row;
  if (at200 <= veryCold) return 'very-cold';
  if (at200 <= cold) return 'cold';
  if (at200 >= veryHot) return 'very-hot';
  if (at200 >= hot) return 'hot';
  return 'normal';
}

/**
 * The whole room read, from a list of completed sales.
 *
 * @param sales           [{ name, pos, posRank, price }] — the Live Board's
 *                        sales array, or any equivalent. Order is irrelevant;
 *                        the tally is a sum.
 * @param settings        { budget } — the LEAGUE budget, per team.
 * @param dynamicPriceMap the usePricing map, only for its __leagueMultipliers
 *                        rider. null is fine and is the uncalibrated path.
 * @param openSlots       roster slots still unfilled ACROSS THE WHOLE LEAGUE.
 *
 * `tally` counts every priced position, DEF and K included, because that is
 * what the engine's inflationIndex has always counted. `byPos` reports only the
 * four positions worth showing a user.
 */
export function roomTemp(sales, settings, dynamicPriceMap, openSlots) {
  let tally = 0;
  let nSold = 0;
  let unpriced = 0;
  const byPos = { QB: 0, RB: 0, WR: 0, TE: 0 };
  for (const sale of sales || []) {
    nSold++;
    const mid = tierMidDollars(sale, settings, dynamicPriceMap);
    if (mid == null) { unpriced++; continue; }
    const delta = (sale.price || 0) - mid;
    tally += delta;
    if (byPos[sale.pos] !== undefined) byPos[sale.pos] += delta;
  }
  const perSlot = openSlots > 0 ? tally / openSlots : null;
  return {
    tally,
    perSlot,
    band: roomBand(perSlot, nSold, openSlots, settings?.budget),
    nSold,
    unpriced,
    byPos,
  };
}
